import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  FormControl,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Menu as MenuIcon, Logout, Person } from "@mui/icons-material";
import { useAuth } from "../../../resources/AuthProvider";
import { PageType } from "../../../types/Page";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import styles from "./Header.module.css";
import Logo from "../../../assets/logo_siscap.svg";

type HeaderProps = {
  onToggleSidebar?: () => void;
  pages?: PageType[];
  isMobile?: boolean;
};

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, isMobile = false }) => {
  const { user, logout, switchProfile, getDisplayName } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [pendingRoleId, setPendingRoleId] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSwitchingProfile, setIsSwitchingProfile] = useState(false);
  const [switchError, setSwitchError] = useState("");

  useEffect(() => {
    const currentRoleId = user?.active_role?.id || user?.role_id;
    setSelectedRoleId(currentRoleId ? String(currentRoleId) : "");
  }, [user?.active_role?.id, user?.role_id]);

  const switchableRoles = user?.switchable_roles || [];

  const pendingRole = useMemo(
    () => switchableRoles.find((role) => role.id === pendingRoleId),
    [pendingRoleId, switchableRoles],
  );

  const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleLogoutClick = () => {
    handleClose();
    logout();
    navigate("/login");
  };

  const handleEditProfileClick = () => {
    handleClose();
    navigate("/editar-perfil");
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const nextRoleId = Number(event.target.value);
    const currentRoleId = Number(selectedRoleId);

    if (!nextRoleId || nextRoleId === currentRoleId) {
      return;
    }

    setPendingRoleId(nextRoleId);
    setSwitchError("");
    setIsConfirmOpen(true);
  };

  const handleCloseProfileDialog = () => {
    if (isSwitchingProfile) return;
    setIsConfirmOpen(false);
    setPendingRoleId(null);
    setSwitchError("");
  };

  const handleConfirmProfileSwitch = async () => {
    if (!pendingRoleId || isSwitchingProfile) return;

    try {
      setIsSwitchingProfile(true);
      setSwitchError("");
      await switchProfile(pendingRoleId);
      setIsConfirmOpen(false);
      setPendingRoleId(null);
    } catch (error: any) {
      setSwitchError(error.message || "Nao foi possivel alterar o perfil.");
    } finally {
      setIsSwitchingProfile(false);
    }
  };

  const confirmMessage = switchError
    ? `Deseja trocar para o perfil ${pendingRole?.nome || "selecionado"}? ${switchError}`
    : `Deseja trocar para o perfil ${pendingRole?.nome || "selecionado"}?`;

  return (
    <header className={styles.container}>
      <div className={styles.buttonToggle}>
        <IconButton
          onClick={onToggleSidebar}
          className={styles.iconToggle}
          aria-label="Abrir menu"
        >
          <MenuIcon />
        </IconButton>
        {isMobile && (
          <img src={Logo} alt="SISCAP" className={styles.mobileLogo} />
        )}
      </div>

      <div className={styles.blockTheme}>
        <div className={styles.userInfoContainer}>
          <span className={styles.userName}>{getDisplayName() || "-"}</span>
          <span className={styles.userEmail}>{user?.email || "-"}</span>
        </div>

        <div className={styles.userAvatarContainer}>
          <Tooltip title={getDisplayName()}>
            <IconButton onClick={handleMenu} className={styles.userAvatarButton}>
              <Avatar alt={getDisplayName()} src="/avatar.png" sx={{ width: 40, height: 40 }} />
            </IconButton>
          </Tooltip>
        </div>

        {switchableRoles.length > 1 && (
          <div className={styles.profileSwitchContainer}>
            <span className={styles.profileSwitchLabel}>Você está logado(a) como</span>
            <FormControl size="small" className={styles.profileSwitchControl}>
              <Select
                value={selectedRoleId}
                onChange={handleRoleChange}
                displayEmpty
                disabled={isSwitchingProfile}
                className={styles.profileSwitchSelect}
              >
                {switchableRoles.map((role) => (
                  <MenuItem key={role.id} value={String(role.id)}>
                    {role.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem onClick={handleEditProfileClick}>
            <Person fontSize="small" sx={{ mr: 1 }} /> Editar meu perfil
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
          </MenuItem>
        </Menu>
      </div>

      <ConfirmDialog
        open={isConfirmOpen}
        loading={isSwitchingProfile}
        onClose={handleCloseProfileDialog}
        onConfirm={handleConfirmProfileSwitch}
        title="Confirmar mudanca de perfil"
        message={confirmMessage}
        titleButtonConfirm="Confirmar"
        titleButtonClose="Cancelar"
        titleLoadButton="Alterando..."
      />
    </header>
  );
};

export default Header;
